import type { PresentationSection, QuoteFile } from 'paintscout'
import { takeFromCategoryPool, type CategoryPools } from './photo-categories'

export function replaceSectionImages(args: {
  section: PresentationSection
  images: QuoteFile[]
  categoryPools?: CategoryPools
}): {
  section: PresentationSection
  imageCount: number
} {
  let { section } = args
  const { images, categoryPools } = args

  // `images` empty (e.g. no default images configured) previously spun forever here — concatenating
  // an empty array onto itself never grows past length 0, so the loop below never reached 6.
  let loopedImages = [...images]
  while (images.length > 0 && loopedImages.length < 6) {
    loopedImages = [...loopedImages, ...images]
  }

  if (['banner', 'image', 'logo'].includes(section.type)) {
    if (section.type === 'image' && section.fields.image?.format === 'youtube') {
      return {
        section,
        imageCount: 0
      }
    }
    const categoryMatch = takeFromCategoryPool(categoryPools, section.fields.imageCategory)
    if (categoryMatch) {
      // A category match is drawn from its own pool, not the general looped sequence — it must not
      // advance imageCount, or the caller's cursor into `images` would skip a photo it never used.
      return {
        section: { ...section, fields: { ...section.fields, image: { ...section.fields.image, ...categoryMatch } } },
        imageCount: 0
      }
    }
    return {
      section: { ...section, fields: { ...section.fields, image: { ...section.fields.image, ...loopedImages[0] } } },
      imageCount: 1
    }
  }

  if (['2-column', '3-column'].includes(section.type)) {
    let imageCount = 0
    if (section.fields.leftType === 'image') {
      const categoryMatch = takeFromCategoryPool(categoryPools, section.fields.leftImageCategory)
      section = {
        ...section,
        fields: {
          ...section.fields,
          leftImage: { ...section.fields.leftImage, ...(categoryMatch ?? loopedImages[imageCount]) }
        }
      }
      if (!categoryMatch) imageCount++
    }
    if (section.fields.rightType === 'image') {
      const categoryMatch = takeFromCategoryPool(categoryPools, section.fields.rightImageCategory)
      section = {
        ...section,
        fields: {
          ...section.fields,
          rightImage: { ...section.fields.rightImage, ...(categoryMatch ?? loopedImages[imageCount]) }
        }
      }
      if (!categoryMatch) imageCount++
    }
    if (section.fields.centerType === 'image') {
      const categoryMatch = takeFromCategoryPool(categoryPools, section.fields.centerImageCategory)
      section = {
        ...section,
        fields: {
          ...section.fields,
          centerImage: { ...section.fields.centerImage, ...(categoryMatch ?? loopedImages[imageCount]) }
        }
      }
      if (!categoryMatch) imageCount++
    }
    return { section, imageCount }
  }

  if (['cards'].includes(section.type)) {
    let imageCount = 0
    section.fields.cards.forEach((card, index) => {
      if (card.file?.format === 'pdf') return
      const categoryMatch = takeFromCategoryPool(categoryPools, card.imageCategory)
      if (categoryMatch) {
        section.fields.cards[index].file = { ...card.file, ...categoryMatch }
        return
      }
      section.fields.cards[index].file = { ...card.file, ...loopedImages[imageCount] }
      imageCount++
    })
    return { section, imageCount }
  }

  if (['gallery'].includes(section.type)) {
    let imageCount = 0
    section.fields.files.forEach((file: QuoteFile, index: number) => {
      section.fields.files[index] = { ...file, ...loopedImages[imageCount] }
      imageCount++
    })
    return { section, imageCount }
  }

  if (section.type === 'mission') {
    return {
      section: { ...section, fields: { ...section.fields, image: { ...section.fields.image, ...loopedImages[0] } } },
      imageCount: 1
    }
  }

  if (section.type === 'services') {
    let imageCount = 0
    section.fields.rows.forEach((row, index) => {
      section.fields.rows[index] = { ...row, image: { ...row.image, ...loopedImages[imageCount] } }
      imageCount++
    })
    return { section, imageCount }
  }

  if (section.type === 'slider-image') {
    return {
      section: {
        ...section,
        fields: {
          ...section.fields,
          leftImage: { ...section.fields.leftImage, ...loopedImages[0] },
          rightImage: { ...section.fields.rightImage, ...loopedImages[1] }
        }
      },
      imageCount: 2
    }
  }

  // `awards` (badge/certification logos) and `documents` (real PDFs) need specific real-world
  // assets the AI can't fabricate from a generic photo pool — see x-blockGuide `sourceFromWebsite`
  // in presentation-v2.schema.json. Filling their image slots with an unrelated house photo would
  // be actively wrong, not just unfilled, so they're deliberately left untouched here.
  return { section, imageCount: 0 }
}
